import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { api, mediaUrl, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field, inputCls } from "@/admin/fields";

const CATEGORIES = ["Club News", "Training", "Tournament", "Registration", "Special Event", "Coaching Update"];

export default function NewsManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => { setLoading(true); const { data } = await api.get("/admin/news"); setPosts(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const blank = () => ({ title: "", category: "Club News", content: "", featured_image: "", status: "draft", event_date: "", event_location: "" });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/news/${editing.id}`, editing);
      else await api.post("/admin/news", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this post?")) return; await api.delete(`/admin/news/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-extrabold uppercase text-white">News &amp; Events</h1>
        <button onClick={() => setEditing(blank())} data-testid="news-add-button" className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-4 py-2 text-sm font-bold uppercase text-[#0A1626]"><Plus className="h-4 w-4" /> New Post</button>
      </div>
      {loading ? <div className="py-20 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-slate-400" /></div> : (
        <div className="mt-6 grid gap-3">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center gap-4 rounded-xl border border-slate-700 bg-[#151F30] p-4" data-testid={`news-row-${p.id}`}>
              <div className="h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-slate-700">{p.featured_image && <img src={mediaUrl(p.featured_image)} alt="" className="h-full w-full object-cover" />}</div>
              <div className="flex-1">
                <div className="font-semibold text-white">{p.title}</div>
                <div className="text-xs text-slate-400">{p.category} · <span className={p.status === "published" ? "text-green-400" : "text-amber-400"}>{p.status}</span></div>
              </div>
              <button onClick={() => setEditing(p)} data-testid={`news-edit-${p.id}`} className="rounded bg-slate-700 p-1.5 hover:bg-[#1E63D6]"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(p.id)} data-testid={`news-delete-${p.id}`} className="rounded bg-slate-700 p-1.5 hover:bg-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {posts.length === 0 && <p className="py-10 text-center text-slate-500">No posts yet.</p>}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-700 bg-[#151F30] text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">{editing?.id ? "Edit" : "New"} Post</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid gap-4">
              <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Title</label><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="news-title" /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field field={{ key: "category", label: "Category", type: "select", options: CATEGORIES.map((c) => ({ value: c, label: c })) }} value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
                <Field field={{ key: "status", label: "Status", type: "select", options: [{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }] }} value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} />
                <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Event Date (optional)</label><input type="date" className={inputCls} value={(editing.event_date || "").slice(0,10)} onChange={(e) => setEditing({ ...editing, event_date: e.target.value })} /></div>
                <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Event Location (optional)</label><input className={inputCls} value={editing.event_location || ""} onChange={(e) => setEditing({ ...editing, event_location: e.target.value })} /></div>
              </div>
              <Field field={{ key: "featured_image", label: "Featured Image", type: "image" }} value={editing.featured_image} onChange={(v) => setEditing({ ...editing, featured_image: v })} />
              <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Content</label><textarea rows={8} className={inputCls} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} data-testid="news-content" /></div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-300">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="news-save-button" className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-5 py-2 text-sm font-bold uppercase text-[#0A1626]">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
